import {CKEditor} from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {Link, useParams} from "react-router-dom";
import {
    Box,
    Breadcrumbs,
    Button,
    Chip,
    CircularProgress,
    Container,
    Divider,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    List,
    MenuItem,
    Modal,
    Pagination,
    Paper,
    Select,
    Switch,
    TextField,
    Typography
} from "@mui/material";
import {createLesson, deleteLesson, getAdminChapterDetails, updateLesson} from "../api/api";
import {ChapterDetailsResponse, Lesson, LessonContentType} from "../types";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import DeleteConfirmationModal from "../components/DeleteConfirmationModal";
import {useAlert} from "../context/AlertContext";
import AppModalBox from "../components/AppModalBox";
import AddIcon from "@mui/icons-material/Add";
import BreadcrumbLink from "../components/BreadcrumbLink";

type EditorMode = "RICH" | "CODE";

const lessonContentTypes: { value: LessonContentType; labelKey: string }[] = [
    {value: "LECTURE", labelKey: "chapterdetails.lessonTypes.lecture"},
    {value: "VIDEOLESSON", labelKey: "chapterdetails.lessonTypes.videoLesson"},
];

const getLessonContentTypeLabel = (
    lessonContentType: LessonContentType | undefined,
    t: (key: string) => string
) => {
    switch (lessonContentType) {
        case "VIDEOLESSON":
            return t("chapterdetails.lessonTypes.videoLesson");
        case "LECTURE":
        default:
            return t("chapterdetails.lessonTypes.lecture");
    }
};

const ChapterDetailsPage = () => {
    const {t} = useTranslation();

    const {chapterId} = useParams<{ chapterId?: string }>();
    const safeChapterId = chapterId ?? "";
    const {showAlert} = useAlert();

    const [data, setData] = useState<ChapterDetailsResponse | null>(null);

    const [lessons, setLessons] = useState<Lesson[]>([]);
    const [totalPages, setTotalPages] = useState<number>(1);
    const [page, setPage] = useState<number>(1);

    const [openCreateModal, setOpenCreateModal] = useState(false);
    const [openEditModal, setOpenEditModal] = useState(false);

    const [lessonToDelete, setLessonToDelete] = useState<Lesson | null>(null);
    const [editingLesson, setEditingLesson] = useState<Lesson | null>(null);

    const [newLessonTitle, setNewLessonTitle] = useState("");
    const [newLessonOrder, setNewLessonOrder] = useState<number>(1);
    const [newLessonContent, setNewLessonContent] = useState("");
    const [newLessonContentType, setNewLessonContentType] = useState<LessonContentType>("LECTURE");

    const [createMode, setCreateMode] = useState<EditorMode>("RICH");
    const [editMode, setEditMode] = useState<EditorMode>("RICH");
    const [loading, setLoading] = useState(false);
    const [hasError, setHasError] = useState(false);

    const pageSize = 10;

    const fetchData = async (uiPage: number, opts?: { silent?: boolean }) => {
        if (!safeChapterId) return;

        const silent = Boolean(opts?.silent);

        setHasError(false);
        if (!silent) setLoading(true);

        try {
            const res = await getAdminChapterDetails(Number(safeChapterId), uiPage - 1, pageSize);

            setData(res);

            const pageInfo = res.lessons?.page;
            setLessons(res.lessons?.content ?? []);
            setTotalPages(pageInfo?.totalPages ?? 1);
            setPage((pageInfo?.number ?? 0) + 1);
        } catch (e: any) {
            showAlert(e?.message ?? t("chapterdetails.errors.loadFailed"), "error");
            setHasError(true);
            if (!silent) {
                setData(null);
                setLessons([]);
                setTotalPages(1);
                setPage(1);
            }
        } finally {
            if (!silent) setLoading(false);
        }
    };

    useEffect(() => {
        if (!safeChapterId) return;

        let cancelled = false;

        setLoading(true);
        setHasError(false);

        (async () => {
            try {
                const res = await getAdminChapterDetails(Number(safeChapterId), 0, pageSize);

                if (cancelled) return;

                setData(res);

                const pageInfo = res.lessons?.page;
                setLessons(res.lessons?.content ?? []);
                setTotalPages(pageInfo?.totalPages ?? 1);
                setPage((pageInfo?.number ?? 0) + 1);
            } catch (e: any) {
                if (cancelled) return;

                showAlert(e?.message ?? t("chapterdetails.errors.loadFailed"), "error");
                setHasError(true);
                setData(null);
                setLessons([]);
                setTotalPages(1);
                setPage(1);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [safeChapterId]);

    const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
        setLoading(true);
        fetchData(value, {silent: true}).finally(() => setLoading(false));
    };

    const ckeditorSx = (theme: any) => ({
        mb: 2,

        "& .ck.ck-editor": {
            width: "100%",
        },

        "& .ck.ck-editor__main > .ck-editor__editable": {
            minHeight: "300px",
            backgroundColor: `${
                theme.palette.mode === "dark" ? theme.palette.background.default : "#fff"
            } !important`,
            color: `${theme.palette.text.primary} !important`,
            borderColor: `${theme.palette.divider} !important`,
        },

        "& .ck-content": {
            backgroundColor: `${
                theme.palette.mode === "dark" ? theme.palette.background.default : "#fff"
            } !important`,
            color: `${theme.palette.text.primary} !important`,
        },

        "& .ck.ck-toolbar": {
            backgroundColor: `${
                theme.palette.mode === "dark" ? theme.palette.background.paper : "#f5f5f5"
            } !important`,
            borderColor: `${theme.palette.divider} !important`,
            color: `${theme.palette.text.primary} !important`,
        },

        "& .ck.ck-toolbar .ck-button, & .ck.ck-toolbar .ck-button .ck-icon": {
            color: `${theme.palette.text.primary} !important`,
            fill: `${theme.palette.text.primary} !important`,
        },

        "& .ck.ck-dropdown__panel": {
            backgroundColor: `${theme.palette.background.paper} !important`,
            borderColor: `${theme.palette.divider} !important`,
            color: `${theme.palette.text.primary} !important`,
        },
    });

    const codeTextAreaSx = {
        "& .MuiInputBase-input": {
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
            whiteSpace: "pre",
            lineHeight: 1.5,
        },
    };

    const handleAddLesson = () => {
        if (!newLessonTitle.trim() || !safeChapterId || newLessonOrder < 1 || !newLessonContent.trim()) return;

        createLesson({
            title: newLessonTitle,
            orderNum: newLessonOrder,
            chapterId: safeChapterId,
            content: newLessonContent,
            lessonContentType: newLessonContentType,
        })
            .then(() => {
                showAlert(t("chapterdetails.alerts.added"), "success");
                setNewLessonTitle("");
                setNewLessonOrder(lessons.length + 1);
                setNewLessonContent("");
                setNewLessonContentType("LECTURE");
                setCreateMode("RICH");
                setOpenCreateModal(false);
                fetchData(page, {silent: true});
            })
            .catch((e: any) => showAlert(e?.message ?? t("chapterdetails.errors.addFailed"), "error"));
    };

    const handleDeleteLesson = () => {
        if (!lessonToDelete) return;

        deleteLesson(lessonToDelete.id.toString())
            .then(() => {
                showAlert(
                    t("chapterdetails.alerts.deleted", {title: lessonToDelete.title}),
                    "success"
                );
                setLessonToDelete(null);

                const willBeEmpty = lessons.length === 1 && page > 1;
                fetchData(willBeEmpty ? page - 1 : page, {silent: true});
            })
            .catch((e: any) => showAlert(e?.message ?? t("chapterdetails.errors.deleteFailed"), "error"));
    };

    const handleEditLesson = (e: React.FormEvent) => {
        e.preventDefault();

        if (!editingLesson || editingLesson.orderNum <= 0 || !editingLesson.content?.trim()) return;

        updateLesson(editingLesson.id.toString(), {
            title: editingLesson.title,
            orderNum: editingLesson.orderNum,
            chapterId: safeChapterId,
            content: editingLesson.content,
            lessonContentType: editingLesson.lessonContentType ?? "LECTURE",
        })
            .then(() => {
                showAlert(t("chapterdetails.alerts.updated"), "success");
                setEditingLesson(null);
                setOpenEditModal(false);
                fetchData(page, {silent: true});
            })
            .catch((e: any) => showAlert(e?.message ?? t("chapterdetails.errors.updateFailed"), "error"));
    };

    if (loading && !data) {
        return (
            <Container sx={{mt: 6, display: "flex", justifyContent: "center"}}>
                <CircularProgress/>
            </Container>
        );
    }

    if (hasError) {
        return (
            <Container sx={{mt: 6, display: "flex", flexDirection: "column", alignItems: "center", gap: 2}}>
                <Typography color="text.secondary">
                    {t("chapterdetails.errors.loadFailed")}
                </Typography>

                <Box sx={{display: "flex", gap: 2}}>
                    <Button
                        variant="contained"
                        onClick={() => fetchData(page || 1)}
                    >
                        {t("common.retry")}
                    </Button>

                    <Button component={Link} to="/" variant="outlined">
                        {t("common.toMainPage")}
                    </Button>
                </Box>
            </Container>
        );
    }

    if (!data) return null;

    return (
        <Box sx={{position: "relative", px: {xs: 3, md: 3}}}>
            {loading && data && (
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "background.default",
                        opacity: 0.6,
                        zIndex: 10,
                        borderRadius: 2,
                    }}
                >
                    <CircularProgress/>
                </Box>
            )}

            <Breadcrumbs
                separator={<NavigateNextIcon fontSize="small"/>}
                sx={{mb: 2}}
            >
                <BreadcrumbLink to="/">
                    {t("nav.courses")}
                </BreadcrumbLink>

                <BreadcrumbLink to={`/course/${data.courseId}`}>
                    {data.courseTitle || t("chapterdetails.courseDetails")}
                </BreadcrumbLink>

                <Typography color="text.primary" variant="body2">
                    <strong>{data.title || t("chapterdetails.chapterDetails")}</strong>
                </Typography>
            </Breadcrumbs>

            <Box
                sx={{
                    mb: 2,
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 2,
                }}
            >
                <Box sx={{minWidth: 0}}>
                    <Typography sx={{fontSize: 28, fontWeight: 800}} noWrap>
                        {data.title}
                    </Typography>
                </Box>

                <IconButton
                    onClick={() => {
                        setNewLessonOrder(lessons.length + 1);
                        setNewLessonContentType("LECTURE");
                        setCreateMode("RICH");
                        setOpenCreateModal(true);
                    }}
                    aria-label={t("chapterdetails.addLesson")}
                    sx={(theme) => ({
                        width: 44,
                        height: 44,
                        borderRadius: "50%",
                        backgroundColor: theme.palette.action.hover,
                        color: theme.palette.text.primary,
                        flexShrink: 0,
                        "&:hover": {
                            backgroundColor: theme.palette.action.selected,
                        },
                    })}
                >
                    <AddIcon/>
                </IconButton>
            </Box>

            <List sx={{display: "flex", flexDirection: "column", gap: 3}}>
                {lessons.map((lesson) => (
                    <Paper
                        key={lesson.id}
                        elevation={0}
                        sx={(theme) => ({
                            px: 3,
                            py: 2,
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 2,
                            "&:hover": {backgroundColor: theme.palette.action.hover},
                        })}
                    >
                        <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2}}>
                            <Box sx={{minWidth: 0}}>
                                <Box sx={{display: "flex", alignItems: "center", gap: 1, mb: 0.5}}>
                                    <Typography sx={{fontWeight: 700}} noWrap>
                                        {lesson.title}
                                    </Typography>

                                    <Chip
                                        size="small"
                                        label={getLessonContentTypeLabel(lesson.lessonContentType, t)}
                                        variant="outlined"
                                    />
                                </Box>

                                <Typography variant="body2" sx={{color: "text.secondary"}} noWrap>
                                    {t("chapterdetails.order")}: {lesson.orderNum}
                                </Typography>
                            </Box>

                            <Box sx={{display: "flex", alignItems: "center", gap: 2}}>
                                <Button
                                    component={Link}
                                    to={`/lesson/${lesson.id}`}
                                    variant="outlined"
                                    size="small"
                                >
                                    {t("chapterdetails.details")}
                                </Button>

                                <IconButton
                                    onClick={() => {
                                        setEditingLesson({
                                            ...lesson,
                                            lessonContentType: lesson.lessonContentType ?? "LECTURE",
                                        });
                                        setEditMode("RICH");
                                        setOpenEditModal(true);
                                    }}
                                    size="small"
                                    aria-label={t("common.edit")}
                                >
                                    <EditIcon fontSize="small"/>
                                </IconButton>

                                <IconButton
                                    onClick={() => setLessonToDelete(lesson)}
                                    size="small"
                                    aria-label={t("common.delete")}
                                >
                                    <DeleteIcon fontSize="small"/>
                                </IconButton>
                            </Box>
                        </Box>
                    </Paper>
                ))}
            </List>

            <Box sx={{display: "flex", justifyContent: "center", mt: 3}}>
                <Pagination count={totalPages} page={page} onChange={handlePageChange} color="primary"/>
            </Box>

            <DeleteConfirmationModal
                open={lessonToDelete !== null}
                onClose={() => setLessonToDelete(null)}
                onConfirm={handleDeleteLesson}
            />

            <Modal open={openCreateModal} onClose={() => setOpenCreateModal(false)}>
                <AppModalBox
                    sx={{
                        p: 4,
                        width: "60%",
                        maxHeight: "90vh",
                        overflowY: "auto",
                        outline: "none",
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                    }}
                >
                    <Typography variant="h6" sx={{mb: 1}}>
                        {t("chapterdetails.addLesson")}
                    </Typography>

                    <Typography variant="body2" sx={{color: "text.secondary", mb: 2}}>
                        {t("chapterdetails.editorDescription")}
                    </Typography>

                    <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 2}}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={createMode === "CODE"}
                                    onChange={(_, checked) => setCreateMode(checked ? "CODE" : "RICH")}
                                />
                            }
                            label={createMode === "CODE" ? t("chapterdetails.pureCodeMode") : t("chapterdetails.richEditorMode")}
                        />
                    </Box>

                    <Divider sx={{mb: 2}}/>

                    <TextField
                        label={t("chapterdetails.lessonTitle")}
                        fullWidth
                        required
                        value={newLessonTitle}
                        onChange={(e) => setNewLessonTitle(e.target.value)}
                        sx={{mb: 2}}
                    />

                    <FormControl fullWidth required sx={{mb: 2}}>
                        <InputLabel id="create-lesson-content-type-label">
                            {t("chapterdetails.lessonContentType")}
                        </InputLabel>

                        <Select
                            labelId="create-lesson-content-type-label"
                            label={t("chapterdetails.lessonContentType")}
                            value={newLessonContentType}
                            onChange={(e) => setNewLessonContentType(e.target.value as LessonContentType)}
                        >
                            {lessonContentTypes.map((type) => (
                                <MenuItem key={type.value} value={type.value}>
                                    {t(type.labelKey)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        label={t("chapterdetails.order")}
                        type="number"
                        fullWidth
                        required
                        value={newLessonOrder}
                        onChange={(e) => setNewLessonOrder(Number(e.target.value))}
                        sx={{mb: 2}}
                    />

                    <Typography sx={{mb: 1}}>
                        {t("chapterdetails.lessonContent")}
                    </Typography>

                    {createMode === "RICH" ? (
                        <Box sx={ckeditorSx}>
                            <CKEditor
                                editor={ClassicEditor as any}
                                data={newLessonContent}
                                onChange={(_event: any, editor: any) => {
                                    const html = editor.getData();
                                    setNewLessonContent(html);
                                }}
                                config={{mediaEmbed: {previewsInData: true}}}
                            />
                        </Box>
                    ) : (
                        <TextField
                            fullWidth
                            multiline
                            minRows={14}
                            value={newLessonContent}
                            onChange={(e) => setNewLessonContent(e.target.value)}
                            placeholder={t("chapterdetails.codePlaceholder")}
                            sx={{mb: 2, ...codeTextAreaSx}}
                            inputProps={{spellCheck: false}}
                        />
                    )}

                    <Box sx={{display: "flex", gap: 2}}>
                        <Button variant="contained" onClick={handleAddLesson}>
                            {t("common.create")}
                        </Button>

                        <Button variant="outlined" onClick={() => setOpenCreateModal(false)}>
                            {t("common.cancel")}
                        </Button>
                    </Box>
                </AppModalBox>
            </Modal>

            <Modal open={openEditModal} onClose={() => setOpenEditModal(false)}>
                <AppModalBox
                    sx={{
                        p: 4,
                        width: "60%",
                        maxHeight: "90vh",
                        overflowY: "auto",
                        outline: "none",
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                    }}
                >
                    <Typography variant="h6" sx={{mb: 1}}>
                        {t("chapterdetails.editLesson")}
                    </Typography>

                    {editingLesson && (
                        <>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: 2,
                                    mb: 2
                                }}
                            >
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={editMode === "CODE"}
                                            onChange={(_, checked) => setEditMode(checked ? "CODE" : "RICH")}
                                        />
                                    }
                                    label={editMode === "CODE" ? t("chapterdetails.pureCodeMode") : t("chapterdetails.richEditorMode")}
                                />

                                <Typography variant="caption" sx={{color: "text.secondary"}}>
                                    {editMode === "CODE" ? t("chapterdetails.rawHtmlHint") : t("chapterdetails.ckeditorHint")}
                                </Typography>
                            </Box>

                            <Divider sx={{mb: 2}}/>

                            <form onSubmit={handleEditLesson}>
                                <TextField
                                    label={t("chapterdetails.lessonTitle")}
                                    fullWidth
                                    required
                                    value={editingLesson.title}
                                    onChange={(e) =>
                                        setEditingLesson((prev) => (
                                            prev ? {...prev, title: e.target.value} : prev
                                        ))
                                    }
                                    sx={{mb: 2}}
                                />

                                <FormControl fullWidth required sx={{mb: 2}}>
                                    <InputLabel id="edit-lesson-content-type-label">
                                        {t("chapterdetails.lessonContentType")}
                                    </InputLabel>

                                    <Select
                                        labelId="edit-lesson-content-type-label"
                                        label={t("chapterdetails.lessonContentType")}
                                        value={editingLesson.lessonContentType ?? "LECTURE"}
                                        onChange={(e) =>
                                            setEditingLesson((prev) =>
                                                prev
                                                    ? {
                                                        ...prev,
                                                        lessonContentType: e.target.value as LessonContentType,
                                                    }
                                                    : prev
                                            )
                                        }
                                    >
                                        {lessonContentTypes.map((type) => (
                                            <MenuItem key={type.value} value={type.value}>
                                                {t(type.labelKey)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <TextField
                                    label={t("chapterdetails.order")}
                                    type="number"
                                    fullWidth
                                    required
                                    value={editingLesson.orderNum}
                                    onChange={(e) =>
                                        setEditingLesson((prev) => (
                                            prev
                                                ? {
                                                    ...prev,
                                                    orderNum: Number(e.target.value),
                                                }
                                                : prev
                                        ))
                                    }
                                    sx={{mb: 2}}
                                />

                                <Typography sx={{mb: 1}}>
                                    {t("chapterdetails.lessonContent")}
                                </Typography>

                                {editMode === "RICH" ? (
                                    <Box sx={ckeditorSx}>
                                        <CKEditor
                                            editor={ClassicEditor as any}
                                            data={editingLesson.content ?? ""}
                                            onChange={(_event: any, editor: any) => {
                                                const html = editor.getData();
                                                setEditingLesson((prev) => (
                                                    prev ? {...prev, content: html} : prev
                                                ));
                                            }}
                                            config={{mediaEmbed: {previewsInData: true}}}
                                        />
                                    </Box>
                                ) : (
                                    <TextField
                                        fullWidth
                                        multiline
                                        minRows={14}
                                        value={editingLesson.content ?? ""}
                                        onChange={(e) =>
                                            setEditingLesson((prev) => (
                                                prev
                                                    ? {
                                                        ...prev,
                                                        content: e.target.value,
                                                    }
                                                    : prev
                                            ))
                                        }
                                        sx={{mb: 2, ...codeTextAreaSx}}
                                        inputProps={{spellCheck: false}}
                                    />
                                )}

                                <Box sx={{display: "flex", gap: 2, mt: 1}}>
                                    <Button type="submit" variant="contained">
                                        {t("chapterdetails.saveChanges")}
                                    </Button>

                                    <Button variant="outlined" onClick={() => setOpenEditModal(false)}>
                                        {t("common.cancel")}
                                    </Button>
                                </Box>
                            </form>
                        </>
                    )}
                </AppModalBox>
            </Modal>
        </Box>
    );
};

export default ChapterDetailsPage;